import type { GuestSession, Location } from '@/api/types';

/**
 * Where the guest session lives between page loads.
 *
 * This exists because of the payment gateways. ParPay and GlobalPay both hand off to a hosted page, so the
 * browser leaves this origin entirely and comes back — every scrap of in-memory state is gone when it does.
 * The legacy app persisted its context reducers to localStorage for exactly this reason; it looked like
 * sloppiness and was load-bearing.
 *
 * `sessionStorage` would be the tighter choice, but it does not reliably survive a cross-origin redirect
 * round-trip on iOS Safari, which is the single most common browser this app will ever see.
 */
const STORAGE_KEY = 'touchtab.guest-session';

/**
 * Treat a token as expired slightly early. A token with four seconds left will pass a check here and then be
 * rejected by the API mid-request, which surfaces to the guest as a random failure rather than as a clean
 * re-exchange.
 */
const EXPIRY_SKEW_MS = 30_000;

export interface StoredSession {
    accessToken: string;
    /** Epoch milliseconds. Derived from the API's `expiresIn` at the moment the session was created. */
    expiresAt: number;
    location: Location;
}

/** Turns the API's relative `expiresIn` into an absolute instant we can check after a redirect. */
export function toStoredSession(session: GuestSession, now: number = Date.now()): StoredSession {
    return {
        accessToken: session.accessToken,
        expiresAt: now + session.expiresIn * 1000,
        location: session.location,
    };
}

export function isExpired(session: StoredSession, now: number = Date.now()): boolean {
    return session.expiresAt - EXPIRY_SKEW_MS <= now;
}

export function saveSession(session: StoredSession): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch {
        // Private browsing, or storage full. The session stays in memory for this page load, which is enough
        // to view and pay a check — it only breaks if the guest is then redirected to a hosted payment page.
        // Failing the whole flow here would be worse than degrading.
    }
}

/**
 * Returns the stored session, or null when there isn't a usable one.
 *
 * Anything unreadable — missing, malformed, half-written, from an older shape of this app — is treated as
 * absent and cleared. A guest can always recover by rescanning the QR code on their check, so discarding
 * doubtful state is cheap and keeps a stale token from producing confusing 401s later.
 */
export function loadSession(now: number = Date.now()): StoredSession | null {
    let raw: string | null = null;

    try {
        raw = localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }

    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw) as Partial<StoredSession>;

        if (
            typeof parsed?.accessToken !== 'string' ||
            !parsed.accessToken ||
            typeof parsed.expiresAt !== 'number' ||
            !parsed.location?.id
        ) {
            clearSession();
            return null;
        }

        const session = parsed as StoredSession;

        if (isExpired(session, now)) {
            clearSession();
            return null;
        }

        return session;
    } catch {
        clearSession();
        return null;
    }
}

export function clearSession(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Nothing useful to do; the caller is already treating the session as gone.
    }
}
